import React, { useState } from 'react';
import {
  UserCheck,
  Building2,
  ShieldCheck,
  Search,
  Send,
  Lock,
  CheckCircle2,
  XCircle,
  Scale,
  ArrowRight,
  ChevronRight,
  Sparkles,
  Layers,
  FileCheck,
  MailCheck,
  Eye,
  KeyRound,
  Check
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Overview', icon: Sparkles },
  { id: 'candidate-journey', title: '2. For Candidates (7 Steps)', icon: UserCheck },
  { id: 'recruiter-journey', title: '3. For Recruiters (7 Steps)', icon: Building2 },
  { id: 'privacy-first', title: '4. Privacy-First Hiring', icon: Lock },
  { id: 'why-different', title: '5. Traditional vs Nexgent', icon: Scale },
  { id: 'workflow-summary', title: '6. End-to-End Workflow', icon: Layers },
];

const candidateSteps = [
  {
    step: 'Step 1',
    title: 'Create Your Profile',
    desc: 'Register on Nexgent and provide your professional details, including experience, core skills, leadership background, and career preferences.',
    icon: UserCheck
  },
  {
    step: 'Step 2',
    title: 'Verify Your Information',
    desc: 'To maintain platform integrity, candidates verify their email address, professional profile, employment details, and career experience.',
    icon: MailCheck
  },
  {
    step: 'Step 3',
    title: 'Profile Review',
    desc: 'Our administrative team reviews submitted profiles to ensure they meet platform standards and contain accurate executive details.',
    icon: FileCheck
  },
  {
    step: 'Step 4',
    title: 'Become Discoverable',
    desc: 'Once approved, your profile becomes discoverable by verified recruiters matching your specific domain criteria and privacy controls.',
    icon: Eye
  },
  {
    step: 'Step 5',
    title: 'Receive Invitations',
    desc: 'Recruiters send direct invitations expressing interest in your background. You remain in complete control of your exposure.',
    icon: Send
  },
  {
    step: 'Step 6',
    title: 'Review and Respond',
    desc: 'Evaluate received invitations with zero obligation. Accept, decline, or request further role details before making a decision.',
    icon: CheckCircle2
  },
  {
    step: 'Step 7',
    title: 'Connect with Recruiters',
    desc: 'When both parties express mutual intent, direct communications open in accordance with platform policies.',
    icon: KeyRound
  }
];

const recruiterSteps = [
  {
    step: 'Step 1',
    title: 'Create Recruiter Account',
    desc: 'Register your organization and establish a corporate profile using verified company details and contact information.',
    icon: Building2
  },
  {
    step: 'Step 2',
    title: 'Complete Corporate Verification',
    desc: 'To protect candidate privacy, recruiters undergo verification of business email domains, identity, and company credentials.',
    icon: ShieldCheck
  },
  {
    step: 'Step 3',
    title: 'Select Subscription Tier',
    desc: 'Choose an active monthly plan (Ignite, Accelerate, Dominate) to access search capabilities and credit allocations.',
    icon: Layers
  },
  {
    step: 'Step 4',
    title: 'Search Leadership Talent',
    desc: 'Leverage granular intent-based filters to pinpoint leadership professionals matching your specific hiring requirements.',
    icon: Search
  },
  {
    step: 'Step 5',
    title: 'Send Profile Invitations',
    desc: 'Utilize monthly credits to dispatch direct invitations to target candidates while respecting initial profile anonymity.',
    icon: Send
  },
  {
    step: 'Step 6',
    title: 'Candidate Review Phase',
    desc: 'The candidate reviews your company details and position context, deciding whether they wish to evaluate the opportunity.',
    icon: Eye
  },
  {
    step: 'Step 7',
    title: 'Unlock Direct Contact Details',
    desc: 'Upon candidate acceptance, eligible contact information unlocks, enabling direct high-intent hiring discussions.',
    icon: KeyRound
  }
];

export const HowItWorks: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                PLATFORM ARCHITECTURE
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              How Nexgent Works
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <span>Confidential & Mutual Engagement</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Process Navigation
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1: Overview */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Sparkles className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. A Private Leadership Hiring Process</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              Nexgent is engineered to connect experienced professionals with verified corporate recruiters through a confidential, highly structured hiring framework. 
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Unlike public job boards that rely on mass application broadcasts, Nexgent ensures profiles are reviewed, corporate recruiter identities are verified, and connections occur strictly through <strong>mutual intent</strong>.
            </p>
          </section>

          {/* Section 2: For Candidates */}
          <section id="candidate-journey" className="scroll-mt-28 space-y-6 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserCheck className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">2. The Candidate Journey</h2>
                <p className="text-xs text-slate-400">Step-by-step path for executive leadership talent</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 pt-2">
              {candidateSteps.map((s, idx) => {
                const Icon = s.icon;
                return (
                  <div key={idx} className="bg-slate-950/70 border border-slate-800/90 rounded-xl p-4 flex gap-4 items-start">
                    <div className="p-2 bg-slate-900 text-cyan-400 rounded-lg border border-slate-800 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-cyan-400 font-semibold px-2 py-0.5 bg-cyan-950/80 rounded border border-cyan-800/50">
                          {s.step}
                        </span>
                        <h3 className="font-bold text-white text-sm">{s.title}</h3>
                      </div>
                      <p className="text-slate-400 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Section 3: For Recruiters */}
          <section id="recruiter-journey" className="scroll-mt-28 space-y-6 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">3. The Recruiter Journey</h2>
                <p className="text-xs text-slate-400">Structured workflow for verified corporate talent acquisition</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 pt-2">
              {recruiterSteps.map((s, idx) => {
                const Icon = s.icon;
                return (
                  <div key={idx} className="bg-slate-950/70 border border-slate-800/90 rounded-xl p-4 flex gap-4 items-start">
                    <div className="p-2 bg-slate-900 text-cyan-400 rounded-lg border border-slate-800 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-cyan-400 font-semibold px-2 py-0.5 bg-cyan-950/80 rounded border border-cyan-800/50">
                          {s.step}
                        </span>
                        <h3 className="font-bold text-white text-sm">{s.title}</h3>
                      </div>
                      <p className="text-slate-400 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Section 4: Privacy-First */}
          <section id="privacy-first" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Privacy-First Hiring Principles</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent operates around a core architectural principle:
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Core Governance Model</div>
              <p className="text-slate-400 leading-relaxed">
                Candidates maintain explicit visibility preferences. Recruiters gain search access strictly through corporate identity verification, and contact information is disclosed only when mutual interest has been explicitly confirmed by both parties.
              </p>
            </div>
          </section>

          {/* Section 5: Comparison Matrix */}
          <section id="why-different" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Scale className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Why This Process Is Different</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Comparing standard public hiring mechanics against Nexgent's private model:
            </p>

            <div className="overflow-x-auto pt-2">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-mono">
                    <th className="py-3 px-4 font-medium w-1/4">Dimension</th>
                    <th className="py-3 px-4 font-medium text-slate-500 w-3/8">Traditional Job Portals</th>
                    <th className="py-3 px-4 font-medium text-cyan-400 w-3/8">Nexgent Platform</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-slate-300">
                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Access Protocol</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Open public profile databases</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Verified & vetted participants</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Engagement Model</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Mass unverified applications</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Invitation-based engagement</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Privacy Control</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Limited candidate visibility controls</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Granular privacy & access controls</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Market Focus</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Public job-seeking activity</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Curated leadership ecosystem</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Interaction Dynamics</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Unsolicited cold outreach</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Candidate-controlled interactions</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 6: Workflow Visualizer */}
          <section id="workflow-summary" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">6. End-to-End Platform Workflows</h2>
                <p className="text-xs text-slate-400">Linear progression map for both participation paths</p>
              </div>
            </div>

            {/* Candidate Workflow */}
            <div className="space-y-2">
              <h3 className="text-xs font-mono text-cyan-400 font-semibold uppercase">Candidate Progression Pipeline</h3>
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 text-xs text-slate-300 flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Register</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Verify Profile</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Admin Review</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Profile Approval</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Recruiter Discovery</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Invitation Received</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Candidate Response</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-cyan-950 text-cyan-300 font-semibold rounded border border-cyan-800">Connection</span>
              </div>
            </div>

            {/* Recruiter Workflow */}
            <div className="space-y-2 pt-2">
              <h3 className="text-xs font-mono text-cyan-400 font-semibold uppercase">Recruiter Progression Pipeline</h3>
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 text-xs text-slate-300 flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Register</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Verification</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Subscription Purchase</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Search Talent</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Send Invitation</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Candidate Acceptance</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-slate-900 rounded border border-slate-800 text-white font-medium">Contact Access</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="px-2.5 py-1 bg-cyan-950 text-cyan-300 font-semibold rounded border border-cyan-800">Recruitment Process</span>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default HowItWorks;